import { melodyGrids } from '@tunasong/schemas'
import type { MelodyGrid, MelodyMeter } from '@tunasong/schemas'
import { Button, FormControlLabel, Radio, RadioGroup, Select, TextField } from '@tunasong/ui-lib'
import { useRef } from 'react'
import type { ChangeEvent, FC } from 'react'
import { bpmFromTaps } from '../melody-roll/tap-tempo.js'
import { MAX_BARS } from './capture-take.js'
import styles from './capture-settings.module.css'

/**
 * What the take is: how it is played, how fast, in what meter, on what grid, and for how long.
 *
 * Every control writes the whole settings object back - the form owns no state of its own except
 * the tap history, which is not part of the settings. Out-of-range values are DROPPED rather than
 * clamped, the same rule `MelodyRollHeader` states: a half-typed "1" on the way to "12" must not
 * save 1 and then fight the user's next keystroke.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

/**
 * The four ways in. "Sing or hum" and "Instrument" both run the pitch tracker and differ only in
 * what the user is told to do - but they are separate choices because a singer and a guitarist need
 * different words, and because the instrument hint they imply differs. `free` is the click-free
 * take of §11: the same tracker, no metronome, and the grid decided afterwards.
 */
export type CaptureModality = 'midi' | 'voice' | 'instrument' | 'free'

export interface CaptureSettingsValue {
  modality: CaptureModality
  bpm: number
  meter: MelodyMeter
  grid: MelodyGrid
  bars: number
}

const MODALITY_LABEL: Record<CaptureModality, string> = {
  midi: 'MIDI keyboard',
  voice: 'Sing or hum',
  instrument: 'Instrument (guitar, piano)',
  /**
   * The odd one out, knowingly. The other three answer "what are you playing it on" and this one
   * answers "are you playing to a click at all" - two orthogonal questions on one control. §11 asks
   * for it beside them because it is the question a songwriter is actually answering at this moment
   * (hum it now, find the beat afterwards), and a second control whose other value is only ever the
   * default would cost more than it explained.
   */
  free: 'Free tempo (no click)',
}

const GRID_LABEL: Record<MelodyGrid, string> = {
  '8': '1/8',
  '16': '1/16',
  '8t': '1/8 triplet',
  '16t': '1/16 triplet',
}

const BEATS_PER_BAR = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

/** A tap run is finished when the player stops; a fresh tap after this long starts a new count. */
const TAP_RESET_MS = 3000

export interface CaptureSettingsProps {
  value: CaptureSettingsValue
  onChange: (value: CaptureSettingsValue) => void
  /** No MIDI input connected means the MIDI modality would capture nothing. */
  midiAvailable: boolean
  className?: string
}

export const CaptureSettings: FC<CaptureSettingsProps> = props => {
  const { value, onChange, midiAvailable, className } = props
  const taps = useRef<number[]>([])
  const isFreeTempo = value.modality === 'free'

  const handleModality = (_event: ChangeEvent<HTMLInputElement>, modality: string) => {
    onChange({ ...value, modality: modality as CaptureModality })
  }

  const handleBpm = (event: ChangeEvent<HTMLInputElement>) => {
    const bpm = Number(event.target.value)
    if (!Number.isFinite(bpm) || bpm < 20 || bpm > 300) {
      return
    }
    onChange({ ...value, bpm })
  }

  const handleTap = () => {
    const now = performance.now()
    const previous = taps.current[taps.current.length - 1]
    taps.current = previous !== undefined && now - previous > TAP_RESET_MS ? [now] : [...taps.current, now]
    const bpm = bpmFromTaps(taps.current)
    if (bpm !== null) {
      onChange({ ...value, bpm })
    }
  }

  const handleMeterTop = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...value, meter: { ...value.meter, top: Number(event.target.value) } })
  }

  const handleMeterBottom = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...value, meter: { ...value.meter, bottom: event.target.value === '8' ? 8 : 4 } })
  }

  const handleGrid = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...value, grid: event.target.value as MelodyGrid })
  }

  const handleBars = (event: ChangeEvent<HTMLInputElement>) => {
    const bars = Number(event.target.value)
    if (!Number.isInteger(bars) || bars < 1 || bars > MAX_BARS) {
      return
    }
    onChange({ ...value, bars })
  }

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <div className={styles.modality}>
        <span className={styles.legend} id="melody-capture-modality">
          How are you playing it?
        </span>
        <RadioGroup
          row={true}
          name="melody-capture-modality"
          value={value.modality}
          onChange={handleModality}
          aria-labelledby="melody-capture-modality"
        >
          {(['midi', 'voice', 'instrument', 'free'] as const).map(modality => (
            <FormControlLabel
              key={modality}
              label={MODALITY_LABEL[modality]}
              control={<Radio value={modality} disabled={modality === 'midi' && !midiAvailable} />}
            />
          ))}
        </RadioGroup>
      </div>

      {/*
        Nothing in this block is known before a free take. The tempo is what the fit discovers
        afterwards (§11.2), the grid follows from it, and `bars` is a length measured in beats -
        so showing any of them here would be asking the user to decide what they came here to
        avoid deciding.
      */}
      {isFreeTempo ? null : (
        <div className={styles.grid}>
          <TextField
            className={styles.control}
            label="Tempo (BPM)"
            type="number"
            inputProps={{ min: 20, max: 300 }}
            value={value.bpm}
            onChange={handleBpm}
          />
          <Button onClick={handleTap} aria-label="Tap tempo">
            Tap
          </Button>

          <Select className={styles.control} label="Beats per bar" value={value.meter.top} onChange={handleMeterTop}>
            {BEATS_PER_BAR.map(top => (
              <option key={top} value={top}>
                {top}
              </option>
            ))}
          </Select>

          <Select className={styles.control} label="Beat unit" value={value.meter.bottom} onChange={handleMeterBottom}>
            <option value={4}>4</option>
            <option value={8}>8</option>
          </Select>

          <Select className={styles.control} label="Grid" value={value.grid} onChange={handleGrid}>
            {melodyGrids.map(grid => (
              <option key={grid} value={grid}>
                {GRID_LABEL[grid]}
              </option>
            ))}
          </Select>

          <TextField
            className={styles.control}
            label="Bars"
            type="number"
            inputProps={{ min: 1, max: MAX_BARS }}
            value={value.bars}
            onChange={handleBars}
          />
        </div>
      )}
    </div>
  )
}
